import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  HelpCircle, AlertCircle, ChevronRight, Building2, Plus,
  Mail, Copy, X, CheckCircle2, Trash2, Edit2, AtSign
} from 'lucide-react';
import './MissingDocumentsWidget.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Category options
const CATEGORY_OPTIONS = [
  'Financial', 'Legal', 'Tax', 'Operational', 'HR', 'IT', 'Other'
];

const STATUS_CONFIG = {
  Open: { color: '#ef4444', bgColor: '#fee2e2', label: 'Open' },
  Partial: { color: '#f59e0b', bgColor: '#fef3c7', label: 'Partial' },
  Closed: { color: '#10b981', bgColor: '#d1fae5', label: 'Closed' }
};

const PRIORITY_COLORS = {
  High: '#ef4444',
  Medium: '#f59e0b',
  Low: '#6b7280'
};

const IRL_TO_WIDGET_STATUS = { pending: 'Open', in_progress: 'Partial', received: 'Partial', complete: 'Closed', not_applicable: 'Closed' };
const WIDGET_TO_IRL_STATUS = { Open: 'pending', Partial: 'in_progress', Closed: 'complete' };

const normalizeIRLItem = (item) => ({
  id: item.id,
  title: item.description,
  description: item.notes || '',
  category: item.category || 'Financial',
  priority: item.priority ? (item.priority.charAt(0).toUpperCase() + item.priority.slice(1)) : 'Medium',
  dueDate: item.due_date,
  requestedFrom: item.assignee || '',
  assignedTo: item.assignee || '',
  assignedToName: item.assignee || '',
  status: IRL_TO_WIDGET_STATUS[item.status] || 'Open',
  createdAt: item.created_at,
});

function MissingDocumentsWidget({ onViewDiligenceTracker }) {
  const [deals, setDeals] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, partial: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Financial',
    priority: 'Medium',
    dueDate: '',
    requestedFrom: '',
    assignedTo: '',
    assignedToName: ''
  });

  useEffect(() => {
    fetchDeals();
    fetchOrgUsers();
  }, []);

  const fetchOrgUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizations/me/users`, { withCredentials: true });
      setOrgUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching org users:', error);
    }
  };

  useEffect(() => {
    if (selectedDealId) {
      fetchDocumentRequests(selectedDealId);
      fetchOrgUsers(); // refresh in case new members were invited since page load
    }
  }, [selectedDealId]); // eslint-disable-line

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/deals`);
      const dealsData = response.data || [];
      setDeals(dealsData);
      
      if (dealsData.length > 0) {
        setSelectedDealId(dealsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentRequests = async (dealId) => {
    try {
      const response = await axios.get(`${API_URL}/api/deals/${dealId}/irl`, { withCredentials: true });
      const items = (response.data || []).map(normalizeIRLItem);
      setRequests(items);
      const open = items.filter(r => r.status === 'Open').length;
      const partial = items.filter(r => r.status === 'Partial').length;
      const closed = items.filter(r => r.status === 'Closed').length;
      setStats({ total: items.length, open, partial, closed });
    } catch (error) {
      console.error('Error fetching information requests:', error);
      setRequests([]);
    }
  };

  // Sort requests: Open/Partial first, then by due date
  const sortedRequests = useMemo(() => {
    return [...requests].sort((a, b) => {
      // Open/Partial before Closed
      const aActive = a.status === 'Open' || a.status === 'Partial' ? 0 : 1;
      const bActive = b.status === 'Open' || b.status === 'Partial' ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      
      // Then by due date
      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [requests]);

  // Only Open/Partial for display and email
  const activeRequests = useMemo(() => 
    sortedRequests.filter(r => r.status === 'Open' || r.status === 'Partial'),
    [sortedRequests]
  );

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!selectedDealId || !formData.title.trim()) return;

    try {
      await axios.post(`${API_URL}/api/deals/${selectedDealId}/irl`, {
        description: formData.title,
        notes: formData.description,
        category: formData.category,
        priority: formData.priority.toLowerCase(),
        due_date: formData.dueDate || null,
        assignee: formData.assignedToName || formData.requestedFrom || '',
        status: 'pending',
      }, { withCredentials: true });
      fetchDocumentRequests(selectedDealId);
      setShowAddModal(false);
      setFormData({ title: '', description: '', category: 'Financial', priority: 'Medium', dueDate: '', requestedFrom: '', assignedTo: '', assignedToName: '' });
    } catch (error) {
      console.error('Error creating request:', error);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/deals/${selectedDealId}/irl/${requestId}`, {
        status: WIDGET_TO_IRL_STATUS[newStatus] || 'pending',
      }, { withCredentials: true });
      fetchDocumentRequests(selectedDealId);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Delete this information request?')) return;
    try {
      await axios.delete(`${API_URL}/api/deals/${selectedDealId}/irl/${requestId}`, { withCredentials: true });
      fetchDocumentRequests(selectedDealId);
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setFormData({
      title: request.title,
      description: request.description || '',
      category: request.category,
      priority: request.priority,
      dueDate: request.dueDate ? request.dueDate.split('T')[0] : '',
      requestedFrom: request.requestedFrom || '',
      assignedTo: request.assignedTo || '',
      assignedToName: request.assignedToName || ''
    });
    setShowAddModal(true);
  };

  const handleUpdateRequest = async (e) => {
    e.preventDefault();
    if (!editingRequest) return;

    try {
      await axios.put(`${API_URL}/api/deals/${selectedDealId}/irl/${editingRequest.id}`, {
        description: formData.title,
        notes: formData.description,
        category: formData.category,
        priority: formData.priority.toLowerCase(),
        due_date: formData.dueDate || null,
        assignee: formData.assignedToName || formData.requestedFrom || '',
      }, { withCredentials: true });
      fetchDocumentRequests(selectedDealId);
      setShowAddModal(false);
      setEditingRequest(null);
      setFormData({ title: '', description: '', category: 'Financial', priority: 'Medium', dueDate: '', requestedFrom: '', assignedTo: '', assignedToName: '' });
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  // Generate email text
  const emailText = useMemo(() => {
    if (activeRequests.length === 0) return '';
    const dealName = deals.find(d => d.id === selectedDealId)?.name || 'Deal';
    
    const lines = [
      `Subject: Information Request for ${dealName}`,
      '',
      `Dear Team,`,
      '',
      `We are conducting due diligence for ${dealName} and need the following information:`,
      ''
    ];
    
    activeRequests.forEach(item => {
      const dueDate = item.dueDate ? ` (Due: ${new Date(item.dueDate).toLocaleDateString()})` : '';
      lines.push(`• ${item.title}${dueDate}`);
      if (item.description) {
        lines.push(`  ${item.description}`);
      }
      if (item.requestedFrom) {
        lines.push(`  Requested from: ${item.requestedFrom}`);
      }
      if (item.assignedToName) {
        lines.push(`  Assigned to: ${item.assignedToName}`);
      }
      lines.push('');
    });
    
    lines.push('Please provide these at your earliest convenience.');
    lines.push('');
    lines.push('Best regards,');
    
    return lines.join('\n');
  }, [activeRequests, selectedDealId, deals]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewTracker = () => {
    if (!selectedDealId) return;
    const selectedDeal = deals.find(d => d.id === selectedDealId);
    if (selectedDeal && onViewDiligenceTracker) {
      onViewDiligenceTracker(selectedDeal);
    }
  };

  if (loading) {
    return (
      <div className="mdw-widget">
        <div className="mdw-loading">
          <div className="mdw-spinner"></div>
        </div>
      </div>
    );
  }

  if (deals.length === 0) {
    return (
      <div className="mdw-widget">
        <div className="mdw-header">
          <h3><HelpCircle size={18} /> Missing Information Requests</h3>
        </div>
        <div className="mdw-empty">
          <div className="mdw-empty-icon"><Building2 size={32} /></div>
          <p>No deals available</p>
          <span>Create a deal to track missing information</span>
        </div>
      </div>
    );
  }

  const totalMissing = stats.open + stats.partial;

  return (
    <>
      <div className="mdw-widget">
        <div className="mdw-header">
          <h3><HelpCircle size={18} /> Missing Information Requests</h3>
        </div>

        {/* Deal Selector */}
        <div className="mdw-deal-selector">
          <label>Select Deal</label>
          <select 
            className="mdw-deal-select"
            value={selectedDealId || ''}
            onChange={(e) => setSelectedDealId(e.target.value)}
          >
            {deals.map(deal => (
              <option key={deal.id} value={deal.id}>
                {deal.name}
              </option>
            ))}
          </select>
        </div>

        {/* KPIs */}
        <div className="mdw-kpis">
          <div className="mdw-kpi">
            <span className="mdw-kpi-value" style={{ color: '#ef4444' }}>{stats.open}</span>
            <span className="mdw-kpi-label">Missing</span>
          </div>
          <div className="mdw-kpi">
            <span className="mdw-kpi-value" style={{ color: '#f59e0b' }}>{stats.partial}</span>
            <span className="mdw-kpi-label">Partial</span>
          </div>
          <div className="mdw-kpi mdw-kpi-total">
            <span className="mdw-kpi-value">{totalMissing}</span>
            <span className="mdw-kpi-label">Total</span>
          </div>
        </div>

        {/* Add Request Button */}
        <button 
          className="mdw-add-btn"
          onClick={() => {
            setEditingRequest(null);
            setFormData({
              title: '',
              description: '',
              category: 'Financial',
              priority: 'Medium',
              dueDate: '',
              requestedFrom: ''
            });
            setShowAddModal(true);
          }}
        >
          <Plus size={16} />
          Add Information Request
        </button>

        {requests.length === 0 ? (
          <div className="mdw-empty-state">
            <p>No information requests yet.</p>
            <span>Click "Add Information Request" to create one.</span>
          </div>
        ) : totalMissing === 0 ? (
          <div className="mdw-complete-state">
            <CheckCircle2 size={32} color="#10b981" />
            <p>No missing requests. All information received!</p>
          </div>
        ) : (
          <>
            {/* Requests List */}
            <div className="mdw-items-section">
              <h4>Information Requests ({activeRequests.length})</h4>
              <div className="mdw-items-list">
                {activeRequests.map(request => {
                  const statusConfig = STATUS_CONFIG[request.status];
                  return (
                    <div key={request.id} className="mdw-item">
                      <div className="mdw-item-main">
                        <span className="mdw-item-title" title={request.description}>
                          {request.title}
                        </span>
                        <div className="mdw-item-actions">
                          <button 
                            className="mdw-item-btn"
                            onClick={() => handleEditRequest(request)}
                            title="Edit"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button 
                            className="mdw-item-btn mdw-item-btn-delete"
                            onClick={() => handleDeleteRequest(request.id)}
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="mdw-item-meta">
                        <span className="mdw-item-category">{request.category}</span>
                        {request.dueDate && (
                          <span className="mdw-item-due">
                            Due: {new Date(request.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        <select
                          className="mdw-status-select"
                          value={request.status}
                          onChange={(e) => handleUpdateStatus(request.id, e.target.value)}
                          style={{ 
                            backgroundColor: statusConfig.bgColor,
                            color: statusConfig.color
                          }}
                        >
                          <option value="Open">Open</option>
                          <option value="Partial">Partial</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </div>
                      {request.requestedFrom && (
                        <div className="mdw-item-requested">
                          Requested from: {request.requestedFrom}
                        </div>
                      )}
                      {request.assignedToName && (
                        <div className="mdw-item-assigned" style={{ fontSize: 12, color: '#4B5563', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <AtSign size={11} /> {request.assignedToName}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mdw-actions">
              <button className="mdw-view-btn" onClick={handleViewTracker}>
                View Information Tracker
                <ChevronRight size={16} />
              </button>
              {activeRequests.length > 0 && (
                <button 
                  className="mdw-request-btn" 
                  onClick={() => setShowRequestModal(true)}
                >
                  <Mail size={16} />
                  Email Request
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Request Modal */}
      {showAddModal && (
        <div className="mdw-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="mdw-modal mdw-modal-form" onClick={(e) => e.stopPropagation()}>
            <div className="mdw-modal-header">
              <h3>{editingRequest ? 'Edit' : 'Add'} Information Request</h3>
              <button className="mdw-modal-close" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={editingRequest ? handleUpdateRequest : handleCreateRequest}>
              <div className="mdw-form-content">
                <div className="mdw-form-field">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Revenue breakdown by customer"
                    required
                  />
                </div>
                
                <div className="mdw-form-field">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Additional details about the information needed"
                    rows={3}
                  />
                </div>
                
                <div className="mdw-form-row">
                  <div className="mdw-form-field">
                    <label>Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      {CATEGORY_OPTIONS.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mdw-form-field">
                    <label>Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>
                
                <div className="mdw-form-row">
                  <div className="mdw-form-field">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    />
                  </div>

                  <div className="mdw-form-field">
                    <label>Requested From</label>
                    <input
                      type="text"
                      value={formData.requestedFrom}
                      onChange={(e) => setFormData({...formData, requestedFrom: e.target.value})}
                      placeholder="e.g., Seller, CFO"
                    />
                  </div>
                </div>

                <div className="mdw-form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <AtSign size={14} /> Assign to Team Member
                  </label>
                  <select
                    value={formData.assignedTo}
                    onChange={(e) => {
                      const user = orgUsers.find(u => u.id === e.target.value);
                      setFormData({
                        ...formData,
                        assignedTo: e.target.value,
                        assignedToName: user ? (user.full_name || user.name || user.email) : ''
                      });
                    }}
                  >
                    <option value="">— Unassigned —</option>
                    {orgUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.name || u.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mdw-form-actions">
                <button type="button" className="mdw-btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="mdw-btn-primary">
                  {editingRequest ? 'Update' : 'Create'} Information Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showRequestModal && (
        <div className="mdw-modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="mdw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mdw-modal-header">
              <h3><Mail size={18} /> Email Request</h3>
              <button className="mdw-modal-close" onClick={() => setShowRequestModal(false)}>
                <X size={18} />
              </button>
            </div>
            
            <div className="mdw-modal-content">
              <p className="mdw-modal-subtitle">
                Copy the email below to request information for <strong>{deals.find(d => d.id === selectedDealId)?.name}</strong>
              </p>
              
              <div className="mdw-email-preview">
                <pre>{emailText}</pre>
              </div>
              
              <button 
                className={`mdw-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopyEmail}
              >
                {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MissingDocumentsWidget;
